import React, { useState, useEffect, useCallback } from 'react';
import { styles } from './adminStyles';
import api from '../../../services/api';

const CONTRACT_TEMPLATES = [
    { key: 'masterContractPack', label: 'Master Contract Pack' },
    { key: 'addendum', label: 'Addendum' },
    { key: 'withdrawalNotice', label: 'Withdrawal Notice' },
];

// Reuses only hex values already established elsewhere in the admin UI
// (styles.btnPrimary's teal, AuctionMap's amber/slate status legend) rather
// than introducing new arbitrary colors.
const STATUS_COLORS = {
    draft: '#475569', generated: '#475569', sent: '#F59E0B', viewed: '#F59E0B',
    signed: '#0D9488', declined: '#1E293B', voided: '#1E293B',
};

// Dims a fill/text button when disabled, rather than leaving it visually
// identical to its enabled state (the pre-existing bug this whole restyle
// was fixing — unstyled disabled <button>s fall back to a pale grey browser
// default that looked broken next to the design-system buttons).
const btnStyle = (base, disabled) => (disabled ? { ...base, opacity: 0.45, cursor: 'not-allowed' } : base);

const StatusBadge = ({ status }) => (
    <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem',
        fontWeight: 'bold', color: '#FFFFFF', backgroundColor: STATUS_COLORS[status] || '#475569',
        textTransform: 'capitalize',
    }}>
        {status}
    </span>
);

// Self-contained tab component (own API calls), same convention EventArchive.js
// uses — only the closed-events list is threaded down from AdminDashboard.js.
const ContractsDocuments = ({ events = [] }) => {
    const [subTab, setSubTab] = useState('packs');
    const closedEvents = events.filter((e) => e.status === 'closed');

    // === Contract Packs sub-view ===
    const [selectedEventId, setSelectedEventId] = useState('');
    const [contracts, setContracts] = useState([]);
    const [loadingContracts, setLoadingContracts] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [actioningId, setActioningId] = useState(null);
    const [handbookUploading, setHandbookUploading] = useState(false);

    const loadContracts = useCallback(async (eventId, { silent = false } = {}) => {
        if (!eventId) { setContracts([]); return; }
        if (!silent) setLoadingContracts(true);
        try {
            const res = await api.get(`/contracts/event/${eventId}`);
            setContracts(res.data);
        } catch (err) {
            console.error(err);
            if (!silent) alert(err.response?.data?.msg || 'Failed to load contracts.');
        } finally {
            if (!silent) setLoadingContracts(false);
        }
    }, []);

    useEffect(() => { loadContracts(selectedEventId); }, [selectedEventId, loadContracts]);

    // Contract status moves entirely server-side — a background Signable
    // dispatch finishing, or a webhook flipping sent → viewed/signed/declined
    // — with no push channel into this tab, so without polling the table only
    // ever reflects whatever it looked like at the moment this sub-tab was
    // opened. Silent (no spinner/alert) so it doesn't flicker or interrupt an
    // admin mid-action; only runs while the Contract Packs sub-tab is open.
    useEffect(() => {
        if (subTab !== 'packs' || !selectedEventId) return undefined;
        const interval = setInterval(() => loadContracts(selectedEventId, { silent: true }), 20000);
        return () => clearInterval(interval);
    }, [subTab, selectedEventId, loadContracts]);

    const handleGenerate = async () => {
        if (!selectedEventId) return;
        setGenerating(true);
        try {
            const res = await api.post(`/contracts/${selectedEventId}/generate`);
            // Always surface the backend's msg, even on a no-op (e.g. zero
            // eligible farmers) — previously this only alerted when
            // skippedCount was truthy, so a farmerless result showed nothing
            // at all with no way to tell "worked as expected" from "silently failed".
            if (res.data.msg) alert(res.data.msg);
            await loadContracts(selectedEventId);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to generate contracts.');
        } finally {
            setGenerating(false);
        }
    };

    const runContractAction = async (contractId, action) => {
        setActioningId(contractId);
        try {
            const res = await action();
            if (res?.data?.signableVoidWarning) alert(res.data.signableVoidWarning);
            await loadContracts(selectedEventId);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Action failed.');
        } finally {
            setActioningId(null);
        }
    };

    const handleSend = (contractId) => runContractAction(contractId, () => api.post(`/contracts/${contractId}/send`));
    const handleRegenerate = (contractId) => runContractAction(contractId, () => api.post(`/contracts/${contractId}/regenerate`));
    const handleVoid = (contractId) => {
        const reason = window.prompt('Reason for voiding this contract:');
        if (!reason || !reason.trim()) return;
        runContractAction(contractId, () => api.post(`/contracts/${contractId}/void`, { reason: reason.trim() }));
    };

    const handlePreview = async (contractId) => {
        try {
            const res = await api.get(`/contracts/${contractId}/preview`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(res.data);
            window.open(url, '_blank');
        } catch (err) {
            console.error(err);
            alert('Failed to load contract preview.');
        }
    };

    const handleUploadHandbook = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedEventId) return;
        setHandbookUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await api.put(`/contracts/events/${selectedEventId}/handbook`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            alert('Handbook uploaded — it will be referenced as an appendix on newly generated contracts for this event.');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to upload handbook.');
        } finally {
            setHandbookUploading(false);
            e.target.value = '';
        }
    };

    // === Documents & Settings sub-view ===
    // Org-wide contract defaults (Client.contractSettings) — injected into
    // every generated contract regardless of event, so a client admin sets
    // these once here rather than re-entering them per template edit.
    const [contractSettings, setContractSettings] = useState(null);
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [generalTermsUploading, setGeneralTermsUploading] = useState(false);

    const loadContractSettings = useCallback(async () => {
        setLoadingSettings(true);
        try {
            const res = await api.get('/contracts/client-settings');
            setContractSettings({
                headerTitle: '', contactName: '', contactEmail: '', contactPhone: '',
                generalTermsAndConditions: '', generalTermsFileUrl: '', generalTermsFileName: '',
                ...res.data,
            });
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to load contract settings.');
        } finally {
            setLoadingSettings(false);
        }
    }, []);

    useEffect(() => { if (subTab === 'documents') loadContractSettings(); }, [subTab, loadContractSettings]);

    const handleSettingsChange = (field, value) => setContractSettings((prev) => ({ ...prev, [field]: value }));

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            const { headerTitle, contactName, contactEmail, contactPhone, generalTermsAndConditions } = contractSettings;
            const res = await api.put('/contracts/client-settings', { headerTitle, contactName, contactEmail, contactPhone, generalTermsAndConditions });
            setContractSettings((prev) => ({ ...prev, ...res.data }));
            alert('Contract settings saved — they will apply to newly generated contract packs.');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to save contract settings.');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleUploadGeneralTerms = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setGeneralTermsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await api.put('/contracts/client-settings/general-terms-file', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setContractSettings((prev) => ({ ...prev, ...res.data }));
            alert('General Terms & Conditions document uploaded.');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to upload General Terms & Conditions document.');
        } finally {
            setGeneralTermsUploading(false);
            e.target.value = '';
        }
    };

    // === Templates sub-view ===
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [draftBody, setDraftBody] = useState('');
    const [savingTemplate, setSavingTemplate] = useState(false);

    const loadTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        try {
            const res = await api.get('/contracts/templates');
            setTemplates(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingTemplates(false);
        }
    }, []);

    useEffect(() => { if (subTab === 'templates') loadTemplates(); }, [subTab, loadTemplates]);

    const openEditor = (tpl) => {
        setEditingKey(tpl.key);
        setDraftBody(tpl.bodyHtml || '');
    };

    const handleSaveTemplate = async () => {
        if (!editingKey) return;
        if (!draftBody.trim()) { alert('Template body cannot be empty.'); return; }
        setSavingTemplate(true);
        try {
            await api.put(`/contracts/templates/${editingKey}`, { bodyHtml: draftBody });
            setEditingKey(null);
            await loadTemplates();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Failed to save template.');
        } finally {
            setSavingTemplate(false);
        }
    };

    return (
        <div>
            <h2 style={styles.h2}>Contracts &amp; Documents</h2>
            <p style={styles.textMuted}>Generate, assemble, and dispatch contract packs for winning farmers once an auction has closed, and manage the master contract templates they're built from.</p>

            <div style={{ display: 'flex', gap: '10px', margin: '20px 0' }}>
                <button type="button" style={subTab === 'packs' ? styles.btnPrimary : styles.btnReversed} onClick={() => setSubTab('packs')}>Contract Packs</button>
                <button type="button" style={subTab === 'templates' ? styles.btnPrimary : styles.btnReversed} onClick={() => setSubTab('templates')}>Templates</button>
                <button type="button" style={subTab === 'documents' ? styles.btnPrimary : styles.btnReversed} onClick={() => setSubTab('documents')}>Documents &amp; Settings</button>
            </div>

            {subTab === 'packs' && (
                <div style={styles.card}>
                    <label style={styles.label}>Closed Event</label>
                    <select style={styles.select} value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}>
                        <option value="">Select a closed event…</option>
                        {closedEvents.map((ev) => (
                            <option key={ev._id} value={ev._id}>{ev.name}</option>
                        ))}
                    </select>

                    {selectedEventId && (
                        <>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                                <button type="button" style={styles.btnPrimary} onClick={handleGenerate} disabled={generating}>
                                    {generating ? 'Generating…' : 'Generate Contract Packs'}
                                </button>
                                <label style={{ ...styles.btnReversed, cursor: handbookUploading ? 'wait' : 'pointer' }}>
                                    {handbookUploading ? 'Uploading…' : 'Upload Event Handbook'}
                                    <input type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={handleUploadHandbook} disabled={handbookUploading} />
                                </label>
                                <button type="button" style={styles.btnReversed} onClick={() => loadContracts(selectedEventId)} disabled={loadingContracts}>
                                    {loadingContracts ? 'Refreshing…' : 'Refresh'}
                                </button>
                            </div>

                            {loadingContracts ? (
                                <p style={styles.textMuted}>Loading contracts…</p>
                            ) : contracts.length === 0 ? (
                                <p style={styles.textMuted}>No contract packs generated yet for this event.</p>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Farmer</th>
                                            <th style={styles.th}>Type</th>
                                            <th style={styles.th}>Status</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contracts.map((c) => {
                                            const busy = actioningId === c._id;
                                            return (
                                                <tr key={c._id}>
                                                    <td style={styles.td}>{c.farmer?.name || 'Unknown'}</td>
                                                    <td style={{ ...styles.td, textTransform: 'capitalize' }}>{c.type}</td>
                                                    <td style={styles.td}><StatusBadge status={c.status} /></td>
                                                    <td style={styles.td}>
                                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                            <button type="button" style={btnStyle(styles.smBtnPrimary, !c.pdfFileKey)} disabled={!c.pdfFileKey} onClick={() => handlePreview(c._id)}>Preview</button>
                                                            <button type="button" style={btnStyle(styles.smBtnPrimary, busy)} disabled={busy} onClick={() => handleRegenerate(c._id)}>Regenerate</button>
                                                            <button type="button" style={btnStyle(styles.smBtnPrimary, busy || !c.pdfFileKey || c.status !== 'generated')} disabled={busy || !c.pdfFileKey || c.status !== 'generated'} onClick={() => handleSend(c._id)}>Send for Signature</button>
                                                            <button type="button" style={btnStyle(styles.smBtnDanger, busy || c.status === 'voided')} disabled={busy || c.status === 'voided'} onClick={() => handleVoid(c._id)}>Void</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            )}

            {subTab === 'templates' && (
                <div style={styles.card}>
                    {loadingTemplates ? (
                        <p style={styles.textMuted}>Loading templates…</p>
                    ) : editingKey ? (
                        <>
                            <h3 style={styles.h3}>{CONTRACT_TEMPLATES.find((t) => t.key === editingKey)?.label}</h3>
                            <p style={styles.textMuted}>
                                Tokens: {'{{farmerName}} {{businessName}} {{sbi}} {{farmerAddress}} {{farmerPhone}} {{eventName}} {{clientName}} {{contractHeaderTitle}} {{totalPayout}} {{termsAndConditions}} {{generatedDate}}'}, a repeating {'{{#each bids}}...{{/each}}'} block (field/action/quantity/price), and a repeating {'{{#each fieldMaps}}...{{/each}}'} block — one map per winning field, each with its own {'{{field}}'} label and {'{{mapImageDataUri}}'} image.
                            </p>
                            <p style={styles.textMuted}>
                                Set via the <strong>Documents &amp; Settings</strong> tab: {'{{clientContactName}} {{clientContactEmail}} {{clientContactPhone}} {{generalTermsAndConditions}} {{generalTermsFileName}}'} (each wrapped in a matching {'{{#if}}'} guard above, so they render blank rather than error if unset).
                            </p>
                            <textarea
                                style={{ ...styles.input, height: '400px', fontFamily: 'monospace', fontSize: '0.8rem' }}
                                value={draftBody}
                                onChange={(e) => setDraftBody(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button type="button" style={styles.btnPrimary} onClick={handleSaveTemplate} disabled={savingTemplate}>
                                    {savingTemplate ? 'Saving…' : 'Publish New Version'}
                                </button>
                                <button type="button" style={styles.btnReversed} onClick={() => setEditingKey(null)}>Cancel</button>
                            </div>
                        </>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Template</th>
                                    <th style={styles.th}>Source</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CONTRACT_TEMPLATES.map(({ key, label }) => {
                                    const tpl = templates.find((t) => t.key === key) || { key, source: 'default', bodyHtml: null };
                                    return (
                                        <tr key={key}>
                                            <td style={styles.td}>{label}</td>
                                            <td style={{ ...styles.td, textTransform: 'capitalize' }}>{tpl.source}</td>
                                            <td style={styles.td}>
                                                <button type="button" style={styles.smBtnPrimary} onClick={() => openEditor(tpl)}>Edit</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {subTab === 'documents' && (
                <div style={styles.card}>
                    {loadingSettings || !contractSettings ? (
                        <p style={styles.textMuted}>Loading contract settings…</p>
                    ) : (
                        <>
                            <h3 style={styles.h3}>Contract Header</h3>
                            <p style={styles.textMuted}>Shown as "{'{{clientName}}'} — [this title]" at the top of every generated contract pack.</p>
                            <label style={styles.label}>Contract Title</label>
                            <input
                                style={styles.input}
                                placeholder="Environmental Land Management Contract"
                                value={contractSettings.headerTitle}
                                onChange={(e) => handleSettingsChange('headerTitle', e.target.value)}
                            />

                            <h3 style={{ ...styles.h3, marginTop: '25px' }}>Farmer Contact Point</h3>
                            <p style={styles.textMuted}>Shown on every contract pack so farmers know who to contact with queries.</p>
                            <label style={styles.label}>Contact Name</label>
                            <input style={styles.input} value={contractSettings.contactName} onChange={(e) => handleSettingsChange('contactName', e.target.value)} />
                            <label style={styles.label}>Contact Email</label>
                            <input style={styles.input} type="email" value={contractSettings.contactEmail} onChange={(e) => handleSettingsChange('contactEmail', e.target.value)} />
                            <label style={styles.label}>Contact Phone</label>
                            <input style={styles.input} value={contractSettings.contactPhone} onChange={(e) => handleSettingsChange('contactPhone', e.target.value)} />

                            <h3 style={{ ...styles.h3, marginTop: '25px' }}>General Terms &amp; Conditions</h3>
                            <p style={styles.textMuted}>
                                Applies to every event, on top of that event's own (often summarised) Terms &amp; Conditions. Paste the full text below and/or upload a formal document — both are automatically included on every contract pack generated from now on.
                            </p>
                            <label style={styles.label}>Terms Text</label>
                            <textarea
                                style={{ ...styles.input, height: '160px' }}
                                value={contractSettings.generalTermsAndConditions}
                                onChange={(e) => handleSettingsChange('generalTermsAndConditions', e.target.value)}
                            />

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '10px 0 20px' }}>
                                <label style={{ ...styles.btnReversed, cursor: generalTermsUploading ? 'wait' : 'pointer' }}>
                                    {generalTermsUploading ? 'Uploading…' : 'Upload General T&Cs Document'}
                                    <input type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={handleUploadGeneralTerms} disabled={generalTermsUploading} />
                                </label>
                                {contractSettings.generalTermsFileName && (
                                    <span style={styles.textMuted}>Current: {contractSettings.generalTermsFileName}</span>
                                )}
                            </div>

                            <button type="button" style={styles.btnPrimary} onClick={handleSaveSettings} disabled={savingSettings}>
                                {savingSettings ? 'Saving…' : 'Save Contract Settings'}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContractsDocuments;
